import React, { useCallback, useEffect, useState } from 'react';
import {
  FaTimes, FaSignInAlt, FaLaptop, FaMobileAlt, FaTabletAlt, FaCalendarCheck,
  FaProjectDiagram, FaUsers, FaTicketAlt, FaGraduationCap, FaClipboardList,
  FaBirthdayCake, FaMoneyBillWave, FaSignOutAlt, FaShieldAlt, FaLockOpen,
  FaExclamationTriangle, FaSync, FaHistory, FaKey, FaUserCheck, FaQrcode,
} from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { API_BASE, authFetch } from '../utils/api';
import UserAvatar from './UserAvatar';

/**
 * Click-through detail panel for a single member, shared by
 * /device-management and /session-management.
 *
 * Slides in from the right on desktop and covers the screen on a phone --
 * a full-screen sheet rather than a centered modal, because the three tabs
 * in here (activity, devices, sessions) are lists that need the height.
 */

const TYPE_META = {
  login:        { icon: FaSignInAlt,      color: 'var(--info)' },
  attendance:   { icon: FaCalendarCheck,  color: 'var(--success)' },
  project:      { icon: FaProjectDiagram, color: 'var(--accent)' },
  team:         { icon: FaUsers,          color: 'var(--accent-secondary)' },
  ticket:       { icon: FaTicketAlt,      color: 'var(--warning)' },
  registration: { icon: FaClipboardList,  color: 'var(--info)' },
  verify:       { icon: FaUserCheck,      color: 'var(--success)' },
  checkin:      { icon: FaQrcode,         color: 'var(--warning)' },
  wish:         { icon: FaBirthdayCake,   color: 'var(--accent-secondary)' },
  finance:      { icon: FaMoneyBillWave,  color: 'var(--success)' },
};

const STAT_CARDS = [
  { key: 'logins',         label: 'Logins',        icon: FaSignInAlt },
  { key: 'devices',        label: 'Devices',       icon: FaLaptop },
  { key: 'activeSessions', label: 'Active now',    icon: FaKey },
  { key: 'attendance',     label: 'Attendance',    icon: FaCalendarCheck },
  { key: 'projects',       label: 'Projects',      icon: FaProjectDiagram },
  { key: 'teamRoles',      label: 'Team roles',    icon: FaUsers },
  { key: 'tickets',        label: 'Tickets',       icon: FaTicketAlt },
  { key: 'quizzes',        label: 'Quizzes',       icon: FaGraduationCap },
  { key: 'registrations',  label: 'Registered',    icon: FaClipboardList },
  { key: 'verifications',  label: 'Verified',      icon: FaUserCheck },
  { key: 'checkins',       label: 'Check-ins',     icon: FaQrcode },
  { key: 'birthdayWishes', label: 'Wishes sent',   icon: FaBirthdayCake },
  { key: 'financeEntries', label: 'Finance',       icon: FaMoneyBillWave },
];

function deviceIcon(type) {
  if (type === 'Mobile') return FaMobileAlt;
  if (type === 'Tablet') return FaTabletAlt;
  return FaLaptop;
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function relativeTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

export default function UserActivityPanel({ sliitId, onClose, onChanged, initialTab = 'activity' }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState(initialTab);
  const [busy, setBusy]       = useState(false);
  // Destructive actions (sign out everywhere / restrict login) route through
  // this so nothing irreversible is one tap away on a phone.
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    if (!sliitId) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await authFetch(`${API_BASE}/user_activity.php?sliitid=${encodeURIComponent(sliitId)}`);
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || `Request failed (${res.status})`);
      setData(json);
    } catch (err) {
      setError(err.message || 'Failed to load this member');
    } finally {
      setLoading(false);
    }
  }, [sliitId]);

  useEffect(() => { load(); }, [load]);

  // Escape closes the panel; while it's open the page behind it must not
  // scroll (especially on iOS, where a scrolling backdrop under a sheet
  // reads as the sheet itself being broken).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const profile = data?.profile;
  const stats   = data?.stats || {};
  // The backend already withholds IPs / contact details / admin-only counters
  // from non-admins; this just hides the buttons that would be refused anyway.
  const viewerIsAdmin = !!data?.viewerIsAdmin;

  const runAction = async (fn, successMessage) => {
    setBusy(true);
    try {
      await fn();
      if (successMessage) toast.success(successMessage);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const signOutEverywhere = () => runAction(async () => {
    const res  = await authFetch(`${API_BASE}/logout_device.php`, {
      method: 'POST',
      body: JSON.stringify({ sliitid: sliitId, all: true }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json.error || 'Failed to sign out');
    toast.success(json.message || 'Signed out everywhere');
  });

  const signOutDevice = (device) => runAction(async () => {
    const res  = await authFetch(`${API_BASE}/logout_device.php`, {
      method: 'POST',
      body: JSON.stringify({ logId: device.logId }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json.error || 'Failed to sign out device');
    toast.success(json.message || 'Device signed out');
  });

  const toggleRestrict = () => runAction(async () => {
    const res = await authFetch(`${API_BASE}/toggle_restrict_login.php`, {
      method: 'POST',
      body: JSON.stringify({
        sliitleo_id: profile.leoId,
        current_status: profile.accountBlock,
      }),
    });
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json.message || json.error || 'Failed to update login access');
  }, profile?.accountBlock ? 'Login access restored' : 'Login access restricted');

  const timeline = data?.timeline || [];
  const devices  = data?.devices || [];
  const sessions = data?.sessions || [];

  const tabs = [
    { key: 'activity', label: 'Activity', count: timeline.length },
    { key: 'devices',  label: 'Devices',  count: devices.length },
    { key: 'sessions', label: 'Sessions', count: sessions.length },
  ];

  return (
    <div
      className="modal-overlay ua-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Member detail"
    >
      <div className="panel ua-sheet" onClick={(e) => e.stopPropagation()}>
        {/* ---------- Header ---------- */}
        <div className="ua-head">
          <div className="ua-head-main">
            <UserAvatar
              src={profile?.image}
              alt={profile?.fullName || 'Member'}
              size={48}
              ring="var(--surface-1)"
              fallback="/user.png"
            />
            <div style={{ minWidth: 0 }}>
              <h3 className="truncate" style={{ fontSize: 16 }}>
                {loading ? 'Loading…' : (profile?.fullName || 'Unknown member')}
              </h3>
              <div className="ua-head-meta">
                <span className="num">{sliitId}</span>
                {profile?.role && <><span aria-hidden="true">·</span><span>{profile.role}</span></>}
              </div>
              {profile && (
                <div className="ua-head-pills">
                  {/* accountBlock is withheld from non-admins by the server, so it
                      would always read "allowed" for them -- don't show it at all. */}
                  {viewerIsAdmin && (profile.accountBlock ? (
                    <span className="pill pill-danger"><span className="dot" />Login restricted</span>
                  ) : (
                    <span className="pill pill-success"><span className="dot" />Login allowed</span>
                  ))}
                  {stats.activeSessions > 0 ? (
                    <span className="pill pill-info">
                      <span className="dot" />
                      {stats.activeSessions} active {stats.activeSessions === 1 ? 'session' : 'sessions'}
                    </span>
                  ) : (
                    <span className="pill pill-warning"><span className="dot" />Signed out</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="ua-head-actions">
            <button className="icon-btn" onClick={load} disabled={loading} aria-label="Reload member detail" title="Reload">
              {loading ? <span className="spinner" /> : <FaSync size={13} />}
            </button>
            <button className="icon-btn" onClick={onClose} aria-label="Close panel">
              <FaTimes size={14} />
            </button>
          </div>
        </div>

        {/* ---------- Body ---------- */}
        <div className="ua-body">
          {loading ? (
            <div className="empty-state">
              <span className="spinner lg" />
              <p>Loading member activity…</p>
            </div>
          ) : error ? (
            <div style={{ padding: 'var(--sp-4)' }}>
              <div className="error-banner">
                <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{error}</span>
              </div>
              <button className="btn btn-primary btn-sm" style={{ marginTop: 'var(--sp-3)' }} onClick={load}>
                Try again
              </button>
            </div>
          ) : (
            <>
              {/* Contact strip */}
              {(profile?.email || profile?.contact || profile?.faculty) && (
                <div className="ua-contact">
                  {profile.email &&   <span className="truncate" title={profile.email}>{profile.email}</span>}
                  {profile.contact && <span className="num">{profile.contact}</span>}
                  {profile.faculty && <span className="truncate">{profile.faculty}</span>}
                </div>
              )}

              {/* Stats */}
              <div className="ua-stats">
                {STAT_CARDS.filter(({ key }) => stats[key] !== undefined).map(({ key, label, icon: Icon }) => (
                  <div key={key} className="ua-stat">
                    <div className="ua-stat-top">
                      <Icon size={12} />
                      <span>{label}</span>
                    </div>
                    <div className="ua-stat-value num">{stats[key] ?? 0}</div>
                  </div>
                ))}
                {stats.quizzes > 0 && (
                  <div className="ua-stat">
                    <div className="ua-stat-top"><FaGraduationCap size={12} /><span>Quiz score</span></div>
                    <div className="ua-stat-value num">{stats.quizScore ?? 0}</div>
                  </div>
                )}
              </div>

              {/* Manage (admin only) */}
              {viewerIsAdmin && (
              <div className="ua-manage">
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={busy || stats.activeSessions === 0}
                  onClick={() => setConfirm('signout')}
                  style={{ color: 'var(--error)' }}
                >
                  <FaSignOutAlt size={12} />
                  Sign out everywhere
                </button>
                {profile?.canRestrict ? (
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={busy}
                    onClick={() => setConfirm('restrict')}
                    style={{ color: profile.accountBlock ? 'var(--success)' : 'var(--warning)' }}
                  >
                    {profile.accountBlock ? <FaLockOpen size={12} /> : <FaShieldAlt size={12} />}
                    {profile.accountBlock ? 'Allow login' : 'Restrict login'}
                  </button>
                ) : (
                  <span className="ua-hint">No Leo ID on record — login access can't be changed here.</span>
                )}
              </div>
              )}

              {/* Tabs */}
              <div className="ua-tabs" role="tablist">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    role="tab"
                    aria-selected={tab === t.key}
                    className={`chip ${tab === t.key ? 'active' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label} <span className="num">({t.count})</span>
                  </button>
                ))}
              </div>

              {/* Tab: activity */}
              {tab === 'activity' && (
                timeline.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon"><FaHistory /></div>
                    <h3>Nothing recorded yet</h3>
                    <p>This member hasn't done anything the portal keeps a record of.</p>
                  </div>
                ) : (
                  <ul className="ua-timeline">
                    {timeline.map((item, i) => {
                      const meta = TYPE_META[item.type] || { icon: FaHistory, color: 'var(--text-3)' };
                      const Icon = meta.icon;
                      return (
                        <li key={`${item.type}-${i}`} className="ua-tl-item">
                          <span className="ua-tl-icon" style={{ color: meta.color }}>
                            <Icon size={12} />
                          </span>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="ua-tl-title">{item.title}</div>
                            {item.detail && <div className="ua-tl-detail truncate" title={item.detail}>{item.detail}</div>}
                          </div>
                          <div className="ua-tl-when">
                            <div>{item.at ? relativeTime(item.at) : 'No date'}</div>
                            {item.at && <div className="ua-tl-abs">{formatDateTime(item.at)}</div>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )
              )}

              {/* Tab: devices */}
              {tab === 'devices' && (
                devices.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon"><FaLaptop /></div>
                    <h3>No devices</h3>
                    <p>This member hasn't logged in from anywhere yet.</p>
                  </div>
                ) : (
                  <div className="ua-cards">
                    {devices.map((d) => {
                      const Icon = deviceIcon(d.device?.type);
                      return (
                        <div key={`${d.logId}`} className="ua-card">
                          <div className="ua-card-head">
                            <span className="ua-card-icon"><Icon size={14} /></span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div className="ua-card-title">{d.device?.label || 'Unknown device'}</div>
                              <div className="ua-card-sub num">
                                {d.ipAddress ? `${d.ipAddress} · ` : ''}{d.device?.type || 'Unknown'}
                              </div>
                            </div>
                            {d.activeSessions > 0 ? (
                              <span className="pill pill-success"><span className="dot" />Active</span>
                            ) : (
                              <span className="pill pill-warning"><span className="dot" />Signed out</span>
                            )}
                          </div>
                          <div className="ua-card-grid">
                            <div><span>Logins</span><b className="num">{d.loginCount}</b></div>
                            <div><span>Last used</span><b>{formatDateTime(d.lastUsed)}</b></div>
                            <div><span>First seen</span><b>{formatDateTime(d.firstUsed)}</b></div>
                          </div>
                          {d.userAgent && (
                            <div className="ua-card-ua truncate" title={d.userAgent}>{d.userAgent}</div>
                          )}
                          {viewerIsAdmin && (
                          <div className="ua-card-foot">
                            <button
                              className="btn btn-ghost btn-sm"
                              style={{ color: 'var(--error)' }}
                              disabled={busy || d.activeSessions === 0}
                              onClick={() => signOutDevice(d)}
                            >
                              <FaSignOutAlt size={12} />
                              Sign out this device
                            </button>
                          </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {/* Tab: sessions */}
              {tab === 'sessions' && (
                sessions.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon"><FaKey /></div>
                    <h3>No active sessions</h3>
                    <p>This member is signed out of every device.</p>
                  </div>
                ) : (
                  <div className="ua-cards">
                    {sessions.map((s) => {
                      const Icon = deviceIcon(s.device?.type);
                      return (
                        <div key={s.id} className="ua-card">
                          <div className="ua-card-head">
                            <span className="ua-card-icon"><Icon size={14} /></span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div className="ua-card-title">{s.device?.label || 'Unknown device'}</div>
                              <div className="ua-card-sub num">{s.ipAddress || s.device?.type || ''}</div>
                            </div>
                            <span className="pill pill-success"><span className="dot" />Live</span>
                          </div>
                          <div className="ua-card-grid">
                            <div><span>Signed in</span><b>{formatDateTime(s.createdAt)}</b></div>
                            <div><span>Last seen</span><b>{relativeTime(s.lastSeenAt) || formatDateTime(s.lastSeenAt)}</b></div>
                            <div><span>Expires</span><b>{formatDateTime(s.expiresAt)}</b></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}
            </>
          )}
        </div>

        {/* ---------- Confirmation ---------- */}
        {confirm && (
          <div className="ua-confirm">
            <div className="ua-confirm-box panel">
              <h4>
                {confirm === 'signout'
                  ? 'Sign out of every device?'
                  : profile?.accountBlock ? 'Allow this member to log in?' : 'Restrict this member from logging in?'}
              </h4>
              <p>
                {confirm === 'signout' && (
                  <>
                    <b>{profile?.fullName || 'This member'}</b> will be signed out of all{' '}
                    {stats.activeSessions} {stats.activeSessions === 1 ? 'device' : 'devices'} and must log in again.
                  </>
                )}
                {confirm === 'restrict' && (profile?.accountBlock
                  ? <><b>{profile?.fullName || 'This member'}</b> will be able to log into the portal again.</>
                  : <><b>{profile?.fullName || 'This member'}</b> will be blocked from logging in. Existing sessions stay active until you also sign them out.</>
                )}
              </p>
              <div className="ua-confirm-foot">
                <button className="btn btn-ghost btn-sm" onClick={() => setConfirm(null)} disabled={busy}>Cancel</button>
                <button
                  className={`btn btn-sm ${confirm === 'restrict' && profile?.accountBlock ? 'btn-primary' : 'btn-danger'}`}
                  onClick={confirm === 'signout' ? signOutEverywhere : toggleRestrict}
                  disabled={busy}
                >
                  {busy && <span className="spinner" />}
                  {confirm === 'signout'
                    ? 'Sign out everywhere'
                    : profile?.accountBlock ? 'Allow login' : 'Restrict login'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
